import { useState } from 'react';
import { User } from 'lucide-react';
import type { Profile } from '../types';

interface UserAvatarProps {
  profile?: Partial<Profile> | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  picture?: string | null;
  initials?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  title?: string;
}

export function UserAvatar({
  profile,
  email,
  firstName,
  lastName,
  picture,
  initials,
  size = 'md',
  className = '',
  title
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);

  const effectivePicture = picture !== undefined ? picture : profile?.picture;
  const effectiveEmail = email !== undefined ? email : profile?.email;
  const effectiveFirst = firstName !== undefined ? firstName : profile?.firstName;
  const effectiveLast = lastName !== undefined ? lastName : profile?.lastName;
  const effectiveInitials = initials !== undefined ? initials : profile?.initials;

  const [prevPicture, setPrevPicture] = useState(effectivePicture);
  if (effectivePicture !== prevPicture) {
    setPrevPicture(effectivePicture);
    setImageError(false);
  }

  const sizeClasses = {
    xs: 'w-6 h-6 text-[10px]',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base font-bold'
  }[size];

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  }[size];

  // Compute initials fallback
  let displayInitials = '';
  if (effectiveInitials && effectiveInitials.trim()) {
    displayInitials = effectiveInitials.trim().toUpperCase().substring(0, 3);
  } else if (effectiveFirst || effectiveLast) {
    const f = effectiveFirst?.trim()[0] || '';
    const l = effectiveLast?.trim()[0] || '';
    displayInitials = `${f}${l}`.toUpperCase();
  } else if (effectiveEmail && effectiveEmail.trim()) {
    displayInitials = effectiveEmail.trim().substring(0, 2).toUpperCase();
  }

  const tooltip = title || (
    [effectiveFirst, effectiveLast].filter(Boolean).join(' ') ||
    effectiveEmail ||
    'User'
  );

  return (
    <div
      className={`relative inline-flex items-center justify-center shrink-0 rounded-full overflow-hidden select-none ${sizeClasses} ${className}`}
      title={tooltip}
    >
      {effectivePicture && !imageError ? (
        <img
          src={effectivePicture}
          alt={tooltip}
          onError={() => setImageError(true)}
          className="w-full h-full object-cover rounded-full border border-slate-200/80 shadow-xs"
        />
      ) : displayInitials ? (
        <div className="w-full h-full rounded-full flex items-center justify-center font-bold bg-indigo-100 text-indigo-700 border border-indigo-200/80 shadow-xs">
          {displayInitials}
        </div>
      ) : (
        <div className="w-full h-full rounded-full flex items-center justify-center bg-slate-100 text-slate-500 border border-slate-200 shadow-xs">
          <User className={iconSizes} />
        </div>
      )}
    </div>
  );
}
